import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Helmet from 'components/Helmet';
import MainLayout from 'layouts/MainLayout';
import LinearProgress from 'components/LinearProgress';
import {
  auth,
  googleProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  sendEmailVerification
} from 'services/firebase';
import storage from 'helpers/storage';
import { setAuthToken, hasAuthToken } from 'helpers/authToken';
import { addError, addMessage } from 'actions/error';
import Message from 'components/Snackbars/Message';
import { setReferralKey } from 'actions/referral';
import { ping } from 'actions/app';
import { resolvePostAuthRedirect } from 'helpers/postAuthRedirect';
import Preloader from 'components/Preloader';
import { triggerDailyQuest } from 'actions/gamification';
import { handleTransferOnboarding } from 'actions/onboarding';
import EyeIcon from 'assets/icons/eye.svg?react';
import OrIcon from 'assets/icons/orIcon.svg?react';
import SignIcon from 'assets/icons/sign_arrow.svg?react';
import { DAILY } from 'variables/main';
import { getLocalizedPath } from 'helpers/getLocale';
import getAuthErrorKey from 'helpers/getAuthErrorKey';

const Wrapper = styled('section')(({ theme }) => ({
  width: '400px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  minHeight: 'calc(100vh - 185px)',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '20px',
    minHeight: 'calc(100vh - 135px)'
  }
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: 25,
  marginTop: 60,
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    lineHeight: '42px',
    marginBottom: 30,
    marginTop: 30
  }
}));

const TextFieldWrapper = styled(TextField)(() => ({
  marginBottom: 10,
  width: '100%'
}));

const ButtonWrapper = styled(Button)(({ theme }) => ({
  marginBottom: 10,
  borderRadius: 8,
  width: '100%',
  color: theme.palette.textPrimary.main,
  '&:last-of-type': {
    marginBottom: 60
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: 10,
    '&:last-of-type': {
      marginBottom: 30
    }
  }
}));

const OrIconStyled = styled(OrIcon)(() => ({
  width: '100%',
  marginBottom: '40px',
  marginTop: '40px'
}));

const IconButtonToggleVisibility = styled(IconButton)(() => ({
  height: 28,
  position: 'relative'
}));

const TypographyActinoid = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  '& span': {
    cursor: 'pointer',
    marginLeft: 5,
    borderBottom: `1px solid ${theme.palette.textPrimary.main}`
  }
}));

const SignIconStyled = styled(SignIcon)(({ theme }) => ({
  position: 'absolute',
  top: '50px',
  right: '-320px',
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const SignInScreen = () => {
  const theme = useTheme();
  const t = useTranslate('SignIn');
  const t2 = useTranslate('HELMET');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [isError, setIsError] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  // Set when sign-in succeeds but the email is unverified — drives the
  // "resend verification email" recovery action.
  const [unverifiedUser, setUnverifiedUser] = React.useState(null);

  const redirect = useSelector((state) => state.app.localSignInRedirect);

  // Read (and consume) an explicit funnel redirect target stashed before the
  // auth round trip (survives the email-verification trip, which clears Redux).
  const getExplicitRedirect = React.useCallback(() => {
    const stored = storage.getItem('postAuthRedirect');
    if (stored) storage.removeItem('postAuthRedirect');
    return redirect || stored || '';
  }, [redirect]);

  const setReferral = React.useCallback(async () => {
    const referralKey = storage.getItem('referral');
    if (referralKey) {
      const result = await dispatch(setReferralKey(referralKey));
      if (result instanceof Error) return;
      storage.removeItem('referral');
      dispatch(addMessage(new Message('ReferralSuccess', 'success')));
    }
  }, [dispatch]);

  // Shared post-auth tail: mint the backend session, apply any referral, then
  // route the user — consultants land in the /pro onboarding, others in their
  // profile (an explicit funnel target always wins).
  const completeAuth = React.useCallback(
    async (user) => {
      const idToken = await user.getIdToken(true);
      await setAuthToken(idToken);
      await setReferral();
      let authedUser = null;
      try {
        const res = await dispatch(ping());
        authedUser = res?.user || null;
      } catch (_) {
        // Non-fatal — fall back to the default redirect.
      }
      const dest = await resolvePostAuthRedirect(getExplicitRedirect(), authedUser, dispatch);
      navigate(getLocalizedPath(dest));
      handleTransferOnboarding(dispatch);
      dispatch(triggerDailyQuest(DAILY.login));
    },
    [dispatch, navigate, setReferral, getExplicitRedirect]
  );

  const handleEmailSignIn = React.useCallback(
    async (e) => {
      e.preventDefault();

      if (loading) {
        return;
      }

      setIsError({});
      setUnverifiedUser(null);

      try {
        if (!email || !password) {
          setIsError({
            email: !email,
            password: !password
          });
          return;
        }

        setLoading(true);

        const result = await signInWithEmailAndPassword(auth, email, password);

        const emailVerified = result?.user?.emailVerified;

        if (!emailVerified) {
          setUnverifiedUser(result.user);
          dispatch(addError(new Error('EMAIL_NOT_VERIFIED')));
          setLoading(false);
          return;
        }

        await completeAuth(result.user);

        setLoading(false);
      } catch (error) {
        dispatch(addError(new Error(getAuthErrorKey(error))));
        setLoading(false);
      }
    },
    [dispatch, email, password, loading, completeAuth]
  );

  const handleResendVerification = React.useCallback(async () => {
    if (!unverifiedUser || loading) return;

    try {
      setLoading(true);
      await sendEmailVerification(unverifiedUser);
      dispatch(addMessage(new Message('VerificationEmailResent', 'success')));
    } catch (error) {
      dispatch(addError(new Error(getAuthErrorKey(error))));
    } finally {
      setLoading(false);
    }
  }, [unverifiedUser, loading, dispatch]);

  const handleGoogleSignIn = React.useCallback(async () => {
    if (loading) return;

    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      await completeAuth(result.user);
    } catch (error) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        dispatch(addError(new Error(getAuthErrorKey(error))));
      }
      setLoading(false);
    }
  }, [completeAuth, dispatch, loading]);

  const handlePasswordVisibility = React.useCallback(() => {
    setIsPasswordVisible(!isPasswordVisible);
  }, [isPasswordVisible]);

  // Already-authenticated visitors are bounced to /dashboard, which resolves
  // the role and forwards to the user or counselor dashboard.
  React.useEffect(() => {
    if (hasAuthToken() || storage.getItem('token')) {
      navigate(getLocalizedPath('/dashboard'));
    }
  }, [navigate]);

  // Authenticated users are redirecting away (effect above) — show the
  // preloader rather than briefly flashing the sign-in form.
  if (hasAuthToken() || storage.getItem('token')) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet
        meta={{
          title: t2('signInTitle'),
          description: t2('signInDescription'),
          ogTitle: t2('signInOgTitle'),
          ogDescription: t2('signInOgDescription')
        }}
      />
      <MainLayout>
        <Header />
        <Wrapper>
          <StyledTitle variant="h1">{t('Title')}</StyledTitle>

          <TextFieldWrapper
            placeholder={t('Email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth={true}
            error={isError.email}
            helperText={isError.email ? t('RequiredField') : ''}
          />

          <TextFieldWrapper
            placeholder={t('Password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth={true}
            type={isPasswordVisible ? 'text' : 'password'}
            error={isError.password}
            helperText={isError.password ? t('RequiredField') : ''}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButtonToggleVisibility
                    size="small"
                    sx={
                      isPasswordVisible
                        ? {
                            '&:before': {
                              content: '""',
                              position: 'absolute',
                              top: '4px',
                              left: '13px',
                              width: '1px',
                              height: '21px',
                              backgroundColor: theme.palette.grey[500],
                              transform: 'rotate(-45deg)'
                            }
                          }
                        : null
                    }
                    onClick={handlePasswordVisibility}>
                    <EyeIcon />
                  </IconButtonToggleVisibility>
                )
              }
            }}
          />

          <ButtonWrapper
            onClick={handleEmailSignIn}
            variant="contained"
            color="primary"
            aria-label={t('SignIn')}>
            {t('SignIn')}
          </ButtonWrapper>

          {unverifiedUser ? (
            <ButtonWrapper
              onClick={handleResendVerification}
              variant="outlined"
              color="primary"
              aria-label={t('ResendVerification')}>
              {t('ResendVerification')}
            </ButtonWrapper>
          ) : null}

          <TypographyActinoid variant="body2">
            {t('ToRegister')}
            <span onClick={() => navigate(getLocalizedPath('/sign-up'))}>{t('SignUp')}</span>
          </TypographyActinoid>

          <TypographyActinoid variant="body2" style={{ marginTop: 5 }}>
            {t('ForgotPassword')}
            <span onClick={() => navigate(getLocalizedPath('/forgot-password'))}>
              {t('ResetPassword')}
            </span>
          </TypographyActinoid>

          <OrIconStyled />

          <ButtonWrapper
            onClick={handleGoogleSignIn}
            variant="outlined"
            color="primary"
            aria-label={t('GoogleAuth')}>
            {t('GoogleAuth')}
          </ButtonWrapper>

          {loading ? <LinearProgress /> : null}

          <SignIconStyled />
        </Wrapper>
        <Footer />
      </MainLayout>
    </>
  );
};

export default SignInScreen;
